import React, { useEffect, useState } from 'react';
import { AlertCircleIcon, CheckIcon, GlobeIcon, Loader2Icon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { TextField } from '../../components/ui/Field';
import { SecuritySection } from '../../components/admin/SecuritySection';
import { useAdminData } from '../../contexts/AdminDataContext';
export function Settings() {
  const {
    settings,
    settingsLoading,
    saveSettings
  } = useAdminData();
  const [draft, setDraft] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    setDraft(settings);
  }, [settings]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);
  function field(key: keyof typeof draft) {
    return {
      value: draft[key],
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        setDraft({
          ...draft,
          [key]: event.target.value
        });
        setSaved(false);
        setError('');
      }
    };
  }
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-656" data-mp="2itt0twk1x2at">
      <p className="text-sm text-ink-muted" data-id="element-657" data-mp="2erz8cbk0f4lq">Firmaoplysninger, kontakt og standardpriser</p>
      <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-658" data-mp="2sr78b6nzygqz">
        Indstillinger
      </h1>
      <p className="mt-3 max-w-2xl text-[15px] text-ink-soft" data-id="element-659" data-mp="2nqt0npqb58th">
        Oplysningerne herunder er hjemmesidens ene fælles kilde. De bruges automatisk i topbjælken,
        hero-sektionen, kontaktafsnittet og footeren — du retter dem kun ét sted.
      </p>

      <p className="mt-5 flex max-w-2xl items-start gap-2 rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink-soft" data-id="element-660" data-mp="3bihujais25cj">
        <GlobeIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" data-id="element-661" data-mp="36k6l30we9wuu" />
        Oplysningerne gemmes globalt og vises for alle besøgende på alle enheder.
      </p>

      <form className="mt-7 max-w-2xl space-y-5" onSubmit={async event => {
      event.preventDefault();
      if (saving) return;
      setSaving(true);
      const result = await saveSettings(draft);
      setSaving(false);
      if (result.ok) {
        setSaved(true);
        setError('');
      } else {
        setSaved(false);
        setError(result.error ?? 'Ændringerne kunne ikke gemmes.');
      }
    }} data-id="element-662" data-mp="3ghukw58uxujv">
        <section className="rounded-card border border-line bg-white p-6" data-id="element-663" data-mp="b3e0xd05fzv2">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-664" data-mp="f4fxfm2olxtc">Virksomhed</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-665" data-mp="k3j57ea1qi50">
            <TextField id="in-navn" label="Firmanavn" {...field('companyName')} data-id="element-666" data-mp="k42ds4dt3u6d" />
            <TextField id="in-cvr" label="CVR-nummer" inputMode="numeric" {...field('cvr')} data-id="element-667" data-mp="p1kp21faqlhj" />
            <TextField id="in-tagline" label="Tagline" className="sm:col-span-2" {...field('tagline')} data-id="element-668" data-mp="2lvxlk8wxzejb" />
          </div>
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-669" data-mp="2qw3511t25uqb">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-670" data-mp="2jb9cef3otzip">Kontaktoplysninger</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-671" data-mp="2ei6ha2j9ii0e">
            <TextField id="in-tlf" label="Telefonnummer" type="tel" inputMode="tel" hint="Vises i topbjælken, hero, kontakt og footer." {...field('phone')} data-id="element-672" data-mp="29k4x1ea3ocnc" />
            <TextField id="in-mail" label="E-mail" type="email" {...field('email')} data-id="element-673" data-mp="328k85pq42ate" />
            <TextField id="in-adresse" label="Adresse" className="sm:col-span-2" {...field('address')} data-id="element-674" data-mp="2xas6w53ktccj" />
            <TextField id="in-web" label="Hjemmeside" className="sm:col-span-2" {...field('website')} data-id="element-675" data-mp="2s9b8qhch83zn" />
          </div>
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-676" data-mp="1gvz96inr27ja">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-677" data-mp="1gtwdbhzx1k6n">Standardpriser</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-678" data-mp="1bz0lahuoiwtj">
            <TextField id="in-timepris" label="Timepris (kr.)" inputMode="numeric" {...field('hourlyRate')} data-id="element-679" data-mp="30q23qix7lg76" />
            <TextField id="in-udkald" label="Udkaldsgebyr (kr.)" inputMode="numeric" {...field('callOutFee')} data-id="element-680" data-mp="35ilhbfvwcbbz" />
          </div>
          <p className="mt-4 text-sm text-ink-muted" data-id="element-681" data-mp="2vssastam2mel">
            Bruges som forslag, når du opretter en ny sag. Du kan altid ændre prisen på den enkelte sag.
          </p>
        </section>

        <div className="flex flex-wrap items-center gap-4" data-id="element-682" data-mp="3ofmrdf4b51ms">
          <Button type="submit" size="lg" disabled={!dirty || saving || settingsLoading} data-id="element-683" data-mp="39jf5899c9q4v">
            {saving ? <>
                <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-684" data-mp="3ehgsl6dssbm5" />
                Gemmer …
              </> : 'Gem ændringer'}
          </Button>

          {settingsLoading && <p className="text-[15px] text-ink-muted" data-id="element-685" data-mp="234mnpzw34lbl">Henter aktuelle oplysninger …</p>}

          {dirty && !saving && !saved && !error && <p className="text-[15px] text-ink-muted" data-id="element-686" data-mp="g6vj8jjifstd">Der er ændringer, der ikke er gemt.</p>}

          {saved && <p role="status" className="flex items-center gap-2 text-[15px] text-ink" data-id="element-687" data-mp="u5d5sdxoiz9p">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-688" data-mp="q4jf5csur6d4">
                <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-689" data-mp="2fqg37gxoiea" />
              </span>
              Ændringerne er gemt og opdateret på hjemmesiden
            </p>}

          {error && <p role="alert" className="flex items-start gap-2 text-[15px] text-danger" data-id="element-690" data-mp="b93hyywz6uci">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-691" data-mp="6g0loj0td6sm" />
              {error}
            </p>}
        </div>
      </form>

      <SecuritySection data-id="element-692" data-mp="3ava3hpuxb6lf" />
    </div>;
}